import { useNavigate } from "react-router-dom";
import {
	FileText,
	UserCheck,
	CreditCard,
	Ban,
	Database,
	ShieldAlert,
	Wrench,
	Copyright,
	XCircle,
	History,
	Mail,
	ArrowLeft,
} from "lucide-react";

const LAST_UPDATED = "05 de agosto de 2026";

const sections = [
	{
		icon: UserCheck,
		title: "1. Aceitação dos termos",
		content: (
			<p>
				Ao acessar ou utilizar o SZ Sistemas (SZ PDV), você concorda integralmente com estes Termos de Uso.
				Caso não concorde com alguma condição aqui descrita, não deve utilizar a plataforma. O uso continuado
				do sistema após alterações destes termos representa a aceitação das novas condições.
			</p>
		),
	},
	{
		icon: FileText,
		title: "2. Descrição do serviço",
		content: (
			<p>
				O SZ Sistemas é uma plataforma de gestão em nuvem para comércios, incluindo PDV (ponto de venda),
				controle de estoque, financeiro, relatórios, emissão fiscal e ferramentas complementares de
				comunicação com clientes (WhatsApp, Instagram, entre outros integrados ao sistema).
			</p>
		),
	},
	{
		icon: CreditCard,
		title: "3. Cadastro, planos e cobrança",
		content: (
			<ul className="list-disc pl-6 space-y-1">
				<li>
					Para utilizar o sistema, você deve fornecer informações verdadeiras, completas e atualizadas
					sobre você e/ou sua empresa;
				</li>
				<li>Você é responsável por manter a confidencialidade de login, senha e permissões de acesso;</li>
				<li>
					A contratação está sujeita ao plano escolhido, com valores, periodicidade e forma de pagamento
					informados no momento da assinatura;
				</li>
				<li>
					Não há fidelidade contratual nem multa rescisória, salvo condição comercial diversa acordada
					expressamente;
				</li>
				<li>O não pagamento pode acarretar suspensão ou cancelamento do acesso ao sistema.</li>
			</ul>
		),
	},
	{
		icon: Ban,
		title: "4. Uso adequado da plataforma",
		content: (
			<>
				<p>Ao utilizar o SZ Sistemas, você concorda em não:</p>
				<ul className="list-disc pl-6 space-y-1 mt-3">
					<li>Utilizar o sistema para fins ilícitos, fraudulentos ou que violem direitos de terceiros;</li>
					<li>Tentar acessar áreas, dados ou contas de outros clientes sem autorização;</li>
					<li>Realizar engenharia reversa, copiar ou explorar comercialmente o software sem autorização;</li>
					<li>
						Sobrecarregar, interromper ou comprometer a segurança e o funcionamento da infraestrutura do
						sistema.
					</li>
				</ul>
			</>
		),
	},
	{
		icon: Database,
		title: "5. Dados inseridos no sistema",
		content: (
			<p>
				Todos os cadastros, vendas, clientes, produtos e demais informações inseridas por você ou sua equipe
				pertencem à sua empresa. O SZ Sistemas atua como operador desses dados para viabilizar o
				funcionamento da plataforma, conforme detalhado em nossa{" "}
				<span className="font-semibold text-gray-900">Política de Privacidade</span>.
			</p>
		),
	},
	{
		icon: Wrench,
		title: "6. Disponibilidade e suporte",
		content: (
			<p>
				Envidamos esforços para manter o sistema disponível de forma contínua, mas eventuais interrupções
				para manutenção, atualizações ou motivos técnicos podem ocorrer. Sempre que possível, o PDV opera de
				forma híbrida, permitindo o registro de vendas mesmo em caso de queda de conexão. O suporte técnico é
				prestado em horário comercial pelos canais informados dentro do sistema.
			</p>
		),
	},
	{
		icon: ShieldAlert,
		title: "7. Limitação de responsabilidade",
		content: (
			<p>
				O SZ Sistemas não se responsabiliza por decisões de negócio tomadas com base nos dados e relatórios
				gerados pela plataforma, por perdas decorrentes de uso indevido do sistema, indisponibilidade de
				serviços de terceiros (internet, operadoras de pagamento, órgãos fiscais) ou por informações
				incorretas inseridas pelo próprio usuário.
			</p>
		),
	},
	{
		icon: Copyright,
		title: "8. Propriedade intelectual",
		content: (
			<p>
				O software, sua marca, layout, funcionalidades e demais elementos do SZ Sistemas são de propriedade
				exclusiva da SZ Sistemas, sendo vedada a reprodução, distribuição ou uso não autorizado, total ou
				parcial, sem consentimento prévio por escrito.
			</p>
		),
	},
	{
		icon: XCircle,
		title: "9. Cancelamento e rescisão",
		content: (
			<p>
				Você pode solicitar o cancelamento da sua assinatura a qualquer momento pelos canais de atendimento.
				A SZ Sistemas também pode suspender ou encerrar o acesso em caso de descumprimento destes Termos de
				Uso ou inadimplência, mediante aviso prévio quando aplicável. Após o encerramento, os dados poderão
				ser mantidos pelo período necessário ao cumprimento de obrigações legais e fiscais, conforme nossa
				Política de Privacidade.
			</p>
		),
	},
	{
		icon: History,
		title: "10. Alterações destes termos",
		content: (
			<p>
				Estes Termos de Uso podem ser atualizados periodicamente para refletir melhorias no sistema ou
				mudanças legais. A data da última atualização é sempre indicada no topo desta página. Recomendamos
				revisá-la com regularidade.
			</p>
		),
	},
];

function TermosDeUso() {
	const navigate = useNavigate();

	return (
		<div className="min-h-screen flex flex-col font-sans bg-gray-50 antialiased">
			{/* Navbar */}
			<header className="w-full bg-white shadow-sm sticky top-0 z-50">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
					<div
						className="flex-shrink-0 flex items-center gap-2 cursor-pointer group"
						onClick={() => navigate("/home")}
					>
						<div className="bg-gradient-to-br from-blue-600 to-blue-900 text-white w-9 h-9 rounded-xl shadow-[0_4px_10px_-2px_rgba(37,99,235,0.4)] flex items-center justify-center transform transition-all group-hover:scale-105 group-hover:-translate-y-0.5 relative z-10 overflow-hidden">
							<span className="font-extrabold text-xl leading-none tracking-tight translate-z-0 drop-shadow-sm px-1 mt-0.5">
								SZ
							</span>
						</div>
						<div className="hidden sm:flex flex-col justify-center antialiased">
							<span className="font-black text-xl leading-none text-gray-900 tracking-tight">
								Sistemas
							</span>
							<span className="text-[10px] font-bold text-blue-600 tracking-widest uppercase leading-none mt-1">
								Gestão Inteligente
							</span>
						</div>
					</div>

					<button
						onClick={() => navigate("/home")}
						className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition-colors text-sm"
					>
						<ArrowLeft size={16} />
						Voltar ao site
					</button>
				</div>
			</header>

			{/* Hero */}
			<section className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-16">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-800/50 border border-blue-400/30 rounded-full mb-6">
						<FileText size={28} />
					</div>
					<h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Termos de Uso</h1>
					<p className="text-blue-100">Última atualização: {LAST_UPDATED}</p>
				</div>
			</section>

			{/* Conteúdo */}
			<main className="flex-1 py-16">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 sm:p-10 mb-8">
						<p className="text-gray-600 leading-relaxed">
							Estes Termos de Uso regem o acesso e a utilização da plataforma{" "}
							<strong className="text-gray-900">SZ Sistemas</strong> (SZ PDV) por parte de empresas,
							colaboradores e usuários autorizados. Ao criar uma conta ou utilizar o sistema, você declara
							que leu, compreendeu e concorda com todas as condições descritas abaixo.
						</p>
					</div>

					<div className="space-y-6">
						{sections.map(({ icon: Icon, title, content }) => (
							<div
								key={title}
								className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 sm:p-10 hover:border-blue-200 transition-all"
							>
								<div className="flex items-center gap-3 mb-4">
									<div className="w-10 h-10 shrink-0 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center">
										<Icon size={20} />
									</div>
									<h2 className="text-xl font-bold text-gray-900">{title}</h2>
								</div>
								<div className="text-gray-600 leading-relaxed">{content}</div>
							</div>
						))}

						{/* Contato */}
						<div className="bg-blue-900 text-white rounded-2xl shadow-sm p-8 sm:p-10">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-10 h-10 shrink-0 bg-blue-800/50 border border-blue-400/30 rounded-lg flex items-center justify-center">
									<Mail size={20} />
								</div>
								<h2 className="text-xl font-bold">11. Fale com a gente</h2>
							</div>
							<p className="text-blue-100 leading-relaxed">
								Para dúvidas sobre estes Termos de Uso, entre em contato com a nossa equipe pelo WhatsApp
								através do site ou pelos canais de suporte disponíveis dentro do sistema.
							</p>
						</div>
					</div>
				</div>
			</main>

			{/* Rodapé */}
			<footer className="bg-gray-900 text-gray-400 py-8 border-t-4 border-blue-600">
				<div className="max-w-5xl mx-auto px-6 text-center text-sm">
					<p>© {new Date().getFullYear()} - SZ Sistemas. Todos os direitos reservados.</p>
				</div>
			</footer>
		</div>
	);
}

export default TermosDeUso;
